import { create } from "zustand";
import { createJSONStorage, persist, StateStorage } from "zustand/middleware";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import * as Types from "@/models/types";
import { Clipboard } from "@capacitor/clipboard";
import * as serverClient from "@/models/serverClient";
import { useAuth } from "@/models/useAuth";
import { useUISlice } from "./useUISlice";
import * as gtag from "@/fetchData/gtag";
import { i18n } from "@/locales";
import toast from "react-hot-toast";
import { Purchases, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { downloadFile, getChatId } from "./utils";
import { useRouter } from "@/_router";
import { requestReview } from "./useNative";
import {
  AUDIO_LANGUAGES,
  REVENUE_CAT_ANDROID_API_KEY,
  REVENUE_CAT_IOS_API_KEY,
} from "@/constants";
import { getPlatform, isNativeApp } from "./platform";
import { SubscriptionPlan } from "@/models/types";

let _toastId: string | undefined;

const storage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    const { value } = await Preferences.get({ key: name });
    return value;
  },
  setItem: async (name: string, value: string): Promise<void> => {
    await Preferences.set({ key: name, value });
  },
  removeItem: async (name: string): Promise<void> => {
    await Preferences.remove({ key: name });
  },
};

type State = {
  _hasHydrated: boolean;
  userToken?: string;
  recentAIModels: Types.AIModel[];
  deviceId?: string;
  isSubscribed: boolean;
  remoteConfig?: Types.RemoteConfig;
  userData?: Partial<Types.User>;
  chats: Types.Chat[];
  chatsNextCursor?: string;
};

type Actions = {
  setUserToken: (token: string) => void;
  setHasHydrated: (status: boolean) => void;
  setRecentAIModels: (payload: State["recentAIModels"]) => void;
  setDeviceId: (deviceId: string) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  subscription: () => {
    status: () => Promise<void>;
    revenueCatPurchase: (pkg: PurchasesPackage) => Promise<void>;
    stripeCheckout: (subscriptionPlan: SubscriptionPlan) => Promise<void>;
    stripeManageSubscription: () => Promise<void>;
    onStripeCheckoutSuccess: (params: {
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    revenueCatRestorePurchase: () => Promise<void>;
    configureRevenueCat: (params?: {
      callback?: (isSubscribed: boolean) => void;
    }) => Promise<void>;
  };
  user: () => {
    remoteConfig: (params?: {
      queryParams?: { platform?: string };
    }) => Promise<void>;
    get: () => Promise<void>;
    hasReachedDailyMessageLimit: () => boolean;
    update: (updates: Partial<Types.User>) => Promise<void>;
    access: () => boolean;
    profile: () => Promise<void>;
    changeTranscribeLanguage: () => Promise<void>;
    contactSupport: () => Promise<void>;
  };
  chat: () => {
    sendMessage: (params: {
      file: { url: string; contentType: string; name?: string };
      message: string;
      refreshMessageId?: string;
      aiModelId: Types.AIModel["id"];
      withSearch: boolean;
      router: ReturnType<typeof useRouter>;
    }) => Promise<void>;
    transcribe: (params: {
      fileUrl: string;
      languageCode: string;
    }) => Promise<string>;
    share: (params: {
      chatId: string;
      message?: Types.Chat["messages"][number];
      type: "text" | "pdf" | "markdown";
    }) => Promise<void>;
    copy: (params: {
      chatId: string;
      message?: Types.Chat["messages"][number];
    }) => Promise<void>;
    update: (params: {
      chatId: string;
      updates: Partial<Types.Chat>;
    }) => Promise<void>;
    list: (params?: { nextCursor?: string }) => Promise<void>;
    rename: (chatId: string) => Promise<void>;
    delete: (params: {
      chatId: string;
      callback?: () => void;
    }) => Promise<void>;
  };

  reset: () => void;
};

export const initialState: State = {
  _hasHydrated: false,
  isSubscribed: false,
  recentAIModels: [],
  chats: [],
};

// create store
export const useUserSlice = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initialState,
      setHasHydrated: (status) => {
        set({ _hasHydrated: status });
      },
      setUserToken: (token) => {
        set({ userToken: token });
      },
      setRecentAIModels: (recentAIModels) => {
        set({ recentAIModels });
      },
      setDeviceId: (deviceId) => {
        set({ deviceId });
      },
      setIsSubscribed: (isSubscribed) => {
        set({ isSubscribed });
      },
      setChats: (chats: Types.Chat[]) => {
        set({ chats });
      },
      user: () => ({
        remoteConfig: async (params?: {
          queryParams?: { platform?: string };
        }) => {
          const remoteConfig: Types.RemoteConfig =
            await serverClient.remoteConfigAsync(params);
          if (!remoteConfig) return;

          set({ remoteConfig });
          useUISlice.getState().setCurrentAIModel(remoteConfig.defaultAIModel);
        },
        changeTranscribeLanguage: async () => {
          const voiceLanguage = get().userData?.transcribeLanguage;

          useUISlice.getState().setAlertSelect({
            title: i18n.t("useUserSlice.selectLanguage"),
            options: AUDIO_LANGUAGES.map((language) => language.label),
            defaultOption: AUDIO_LANGUAGES.findIndex(
              (language) => language.value === voiceLanguage
            ),
            onSelect: (index) => {
              const languageCode = AUDIO_LANGUAGES[index].value;
              get()
                .user()
                .update({
                  transcribeLanguage: languageCode?.slice(0, 2)?.toLowerCase(),
                });
            },
          });
        },
        contactSupport: async () => {
          const subject = i18n.t("useUserSlice.contactSupportSubject", {
            deviceId: useUserSlice.getState().deviceId,
          });
          const body = i18n.t("useUserSlice.contactSupportBody");

          window.open(
            `mailto:dkt201474.com@gmail.com?subject=${subject}&body=${body}`,
            "_blank"
          );
        },
        hasReachedDailyMessageLimit: () => {
          if (get().isSubscribed) return false;

          const dailyMessageLimit = get().remoteConfig?.dailyMessageLimit || 3;
          const today = new Date().toISOString().split("T")[0];
          const todayCount = get()?.userData?.dailyMessagesCount?.[today] || 0;
          return todayCount >= dailyMessageLimit;
        },
        get: async () => {
          if (!useAuth.isAuthenticated()) return;

          const user = await serverClient.userAsync({ type: "get" });
          if (!user?.data?.user) return;

          set({ userData: user.data.user });
        },
        access: () => {
          const isSubscribed = get().isSubscribed;

          if (isSubscribed) {
            return true;
          }

          return !get().user().hasReachedDailyMessageLimit();
        },
        profile: async () => {
          if (!useAuth.isAuthenticated()) return;
          get().subscription().status();
          get().user().get();
          get().chat().list();
        },
        update: async (updates) => {
          if (!useAuth.isAuthenticated()) return;

          set({
            userData: { ...get().userData, ...updates },
          });

          const res = await serverClient.userAsync({
            type: "update",
            payload: { updates },
          });

          if (!res?.data?.user) return;

          set({
            userData: res.data.user,
          });
        },
      }),
      chat: () => ({
        transcribe: async ({ fileUrl, languageCode }) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.chatAsync({
            type: "transcribe",
            payload: { fileUrl, languageCode },
          });

          if (!res?.data?.transcriptText) return;

          return res?.data?.transcriptText || "";
        },
        copy: async ({ chatId, message }) => {
          if (!useAuth.isAuthenticated()) return;

          const chat = get().chats.find((chat) => chat.id === chatId);
          if (!chat) return;

          let content = "";

          if (message) {
            content = `${message.role === "user" ? "User: " : "Assistant: "}${message.content}`;
          } else {
            chat.messages.forEach((message) => {
              content += `${message.role === "user" ? "User: " : "Assistant: "}${message.content}\n\n`;
            });
          }

          Clipboard.write({ string: content }).then(() => {
            toast.success(i18n.t("useUserSlice.copied"));
          });
        },
        share: async ({ chatId, type, message }) => {
          if (!useAuth.isAuthenticated()) return;

          const chat = get().chats.find((chat) => chat.id === chatId);
          if (!chat) return;

          let content = "";

          if (message) {
            content = `${message.role === "user" ? "User: " : "Assistant: "}${message.content}`;
          } else {
            chat.messages.forEach((message) => {
              content += `${message.role === "user" ? "User: " : "Assistant: "}${message.content}\n\n`;
            });
          }

          switch (type) {
            case "pdf":
              _toastId = toast.loading(i18n.t("useUserSlice.generatingPDF"));
              const { marked } = await import("marked");
              const htmlContent = await marked(content);
              const res = await serverClient.chatAsync({
                type: "downloadPDF",
                payload: { id: chatId, htmlString: htmlContent },
              });

              if (res?.data?.pdfBase64) {
                await downloadFile({
                  type: "url",
                  url: `data:application/pdf;base64,${res.data.pdfBase64}`,
                  fileName: `${chat.title}.pdf`,
                });
              }

              toast.dismiss(_toastId);
              break;

            case "text":
              downloadFile({
                type: "txt",
                text: content,
                fileName: `${chat.title}.txt`,
              });
              break;

            case "markdown":
              downloadFile({
                type: "txt",
                text: content,
                fileName: `${chat.title}.md`,
              });
              break;
          }
        },
        rename: async (chatId) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertPrompt({
            title: i18n.t("useUserSlice.renameChatTitle"),
            defaultValue:
              get().chats.find((chat) => chat.id === chatId)?.title || "",
            placeholder: i18n.t("useUserSlice.renameChatSubtitle"),
            button: {
              text: i18n.t("useUserSlice.renameChatRenameButton"),
              onPress: (text: string) => {
                if (text.trim().length === 0) return;

                useUserSlice
                  .getState()
                  .chat()
                  .update({ chatId, updates: { title: text.trim() } });
                toast.success(i18n.t("useUserSlice.renameChatSuccess"));
                useUISlice.getState().setAlertPrompt(undefined);
              },
            },
          });
        },
        delete: async ({ chatId, callback }) => {
          if (!useAuth.isAuthenticated()) return;

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.deleteChatTitle"),
            subtitle: i18n.t("useUserSlice.deleteChatSubtitle"),
            cancelText: i18n.t("useUserSlice.cancelButton"),
            button: {
              text: i18n.t("useUserSlice.deleteChatDeleteButton"),
              destructive: true,
              onPress: async () => {
                set({
                  chats: get().chats.filter((chat) => chat.id !== chatId),
                });
                toast.success(i18n.t("useUserSlice.deleteChatSuccess"));
                serverClient.chatAsync({
                  type: "delete",
                  payload: { id: chatId },
                });
                useUISlice.getState().setAlertModal(undefined);
                callback?.();
              },
            },
          });
        },
        list: async (params?: { nextCursor?: string }) => {
          if (!useAuth.isAuthenticated()) return;

          const chats = await serverClient.chatAsync({
            type: "list",
            payload: { nextCursor: params?.nextCursor },
          });

          if (!chats?.data?.chats) return;

          set({
            chats: params?.nextCursor
              ? [...get().chats, ...chats.data.chats]
              : chats.data.chats,
            chatsNextCursor: chats.data.nextCursor,
          });
        },
        sendMessage: async ({
          message,
          router,
          file,
          refreshMessageId,
          aiModelId,
          withSearch,
        }) => {
          const _addLocalMessage = (params: {
            chatId: string;
            messages: Types.Chat["messages"];
          }) => {
            set({
              chats: get().chats.map((chat) =>
                chat.id === params.chatId
                  ? {
                      ...chat,
                      messages: [...chat.messages, ...params.messages],
                    }
                  : chat
              ),
            });
          };

          if (!useAuth.isAuthenticated()) return;

          if (!message.trim()) return;

          let chatId = getChatId();

          if (!chatId) {
            chatId = `chat_${10 ** 14 - new Date().getTime()}`;

            // create new chat locally (optimistic update)
            set({
              chats: [
                {
                  id: chatId,
                  messages: [],
                  title: message.trim().slice(0, 20),
                  userId: useAuth.currentAuthUser().userId,
                  createdAt: new Date().toISOString(),
                },
                ...get().chats,
              ],
            });

            router.replace(`/?roomId=${chatId}`);
          }

          if (refreshMessageId && get().user().access()) {
            const currentChat = get().chats.find((chat) => chat.id === chatId);
            if (!currentChat) return;

            const indexOfRefreshMessage = currentChat.messages.findIndex(
              (m) => m.id === refreshMessageId && m.role === "assistant"
            );

            if (indexOfRefreshMessage === -1) return;

            const nextChatMessages = currentChat.messages.slice(
              0,
              indexOfRefreshMessage - 1 // delete also the user message
            );

            const res = await serverClient.chatAsync({
              type: "update",
              payload: {
                id: chatId,
                updates: {
                  messages: nextChatMessages,
                },
              },
            });

            if (!res?.data?.chat) return;

            set({
              chats: get().chats.map((chat) =>
                chat.id === chatId ? res.data.chat : chat
              ),
            });
          }

          const userMessage: Types.Chat["messages"][number] = {
            id: Date.now().toString(),
            type: "text",
            role: "user",
            fileUrl: file?.url,
            fileContentType: file?.contentType,
            fileName: file?.name,
            aiModel: aiModelId,
            withSearch,
            content: message.trim(),
            createdAt: new Date().toISOString(),
          };
          const assistantMessage: Types.Chat["messages"][number] = {
            id: Date.now().toString(),
            type: "text",
            role: "assistant",
            aiModel: aiModelId,
            withSearch,
            content: "",
            createdAt: new Date().toISOString(),
          };

          if (!get().user().access()) {
            _addLocalMessage({
              chatId,
              messages: [
                userMessage,
                {
                  id: new Date().getTime().toString(),
                  role: "system",
                  content: "",
                  createdAt: new Date().toISOString(),
                  upgradeRequired: true,
                  aiModel: aiModelId,
                  type: "text",
                },
              ],
            });
            useUISlice.getState().setPaywallVisible(true);
            return;
          }

          _addLocalMessage({
            chatId,
            messages: [userMessage, assistantMessage],
          });
          const currentChat = get().chats.find((chat) => chat.id === chatId);
          const previousMessages = currentChat?.messages.slice(
            0,
            currentChat.messages.length - 2
          );

          const res = await serverClient.streamTextAsync({
            chatId,
            previousMessages,
            content: message.trim(),
            fileUrl: file?.url,
            fileContentType: file?.contentType,
            model: aiModelId,
          });

          const reader = res?.body?.getReader();
          if (!reader) return;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const text = new TextDecoder().decode(value);

            const previousStreamText = useUISlice.getState().streamText || "";
            useUISlice.getState().setStreamText(previousStreamText + text);
          }

          reader.cancel();

          const addMessageResponse = await serverClient.chatAsync({
            type: "addMessages",
            payload: {
              chatId,
              messages: [
                userMessage,
                {
                  ...assistantMessage,
                  content: useUISlice.getState().streamText,
                },
              ],
            },
          });

          if (addMessageResponse?.data?.chat) {
            set({
              chats: get().chats.map((chat) =>
                chat.id === chatId ? addMessageResponse.data.chat : chat
              ),
            });
          }
          useUISlice.getState().setStreamText("");
          get().user().get();
          get().subscription().status();
          requestReview();
        },
        update: async ({ chatId, updates }) => {
          if (!useAuth.isAuthenticated()) return;

          if (!get().chats.find((chat) => chat.id === chatId)) return;

          set({
            chats: get().chats.map((chat) =>
              chat.id === chatId ? { ...chat, ...updates } : chat
            ),
          });

          const res = await serverClient.chatAsync({
            type: "update",
            payload: { id: chatId, updates },
          });

          if (res?.data?.chat) {
            set({
              chats: get().chats.map((chat) =>
                chat.id === chatId ? res.data.chat : chat
              ),
            });
          }
        },
      }),
      subscription: () => ({
        configureRevenueCat: async (params?: {
          callback?: (isSubscribed: boolean) => void;
        }) => {
          if (!useAuth.isAuthenticated() || !isNativeApp()) return;

          const user = useAuth.currentAuthUser();

          Purchases.configure({
            appUserID: user?.userId,
            apiKey:
              getPlatform() === "ios"
                ? REVENUE_CAT_IOS_API_KEY
                : REVENUE_CAT_ANDROID_API_KEY,
          }).then(async () => {
            const customerInfo = await Purchases.getCustomerInfo().then(
              (res) => res.customerInfo
            );

            const offerings = await Purchases.getOfferings();

            useUISlice.getState().setRevenueCat({
              packages: offerings?.current?.availablePackages || [],
              customerInfo,
            });

            if (customerInfo?.activeSubscriptions?.length > 0) {
              set({ isSubscribed: true });
              params?.callback?.(true);
            } else {
              set({ isSubscribed: false });
              params?.callback?.(false);
            }
          });
        },
        status: async () => {
          if (!useAuth.isAuthenticated()) {
            set({ isSubscribed: false });
            return;
          }

          const res = await serverClient.subscriptionAsync({
            type: "status",
          });

          if (!res?.data?.subscription) {
            set({ isSubscribed: false });
            return;
          }

          set({ isSubscribed: res?.data?.subscription?.isActive });
        },
        revenueCatPurchase: async (pkg) => {
          if (!useAuth.isAuthenticated()) return;

          try {
            const { customerInfo } = await Purchases.purchasePackage({
              aPackage: pkg,
            });

            if (customerInfo?.activeSubscriptions?.length > 0) {
              set({ isSubscribed: true });

              useUISlice.getState().setPaywallVisible(undefined);
              useUISlice.getState().setRevenueCat({
                ...useUISlice.getState()?.revenueCat,
                customerInfo,
              });
              toast.success(i18n.t("useUserSlice.subscriptionSuccess"));
            }
          } catch (error) {}
        },
        stripeCheckout: async (subscriptionPlan) => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({
            type: "stripeCheckout",
            payload: {
              baseAppUrl: window.location.href,
              plan: subscriptionPlan,
            },
          });

          if (res?.data?.url) {
            window.open(res.data.url, "_self");
          }
        },
        onStripeCheckoutSuccess: async ({ router }) => {
          if (!useAuth.isAuthenticated()) return;

          const slug = router.query.checkout_success;
          if (slug !== "true") return;

          const planPrices: Record<SubscriptionPlan, number> = {
            web_weekly_low_tier: 4.99,
            web_monthly_low_tier: 9.99,
            web_annual_low_tier: 99.99,
          };
          const value =
            planPrices[router.query.plan as SubscriptionPlan] || 4.99;

          gtag.conversion({ value, transactionId: String(Date.now()) });

          useUISlice.getState().setAlertModal({
            title: i18n.t("useUserSlice.subscriptionSuccess"),
            subtitle: i18n.t("useUserSlice.subscriptionSuccessDescription"),
            hideDismissIcon: true,
            shouldPersistBackdropClick: true,
            button: {
              text: i18n.t("useUserSlice.subscriptionSuccessButton"),
              onPress: async () => {
                // Wait for 2 seconds to ensure backend has processed the subscription
                useUISlice.getState().setAlertModal({
                  ...useUISlice.getState().alertModal,
                  loading: true,
                });

                await new Promise((resolve) => setTimeout(resolve, 2000));
                useUISlice.getState().setAlertModal(undefined);
                const url = new URL(window.location.href);
                url.searchParams.delete("checkout_success");
                url.searchParams.delete("plan");
                router.replace(url.pathname);

                get().subscription().status();
              },
            },
          });
        },
        stripeManageSubscription: async () => {
          if (!useAuth.isAuthenticated()) return;

          const res = await serverClient.subscriptionAsync({
            type: "stripeBillingPortal",
            payload: {
              baseAppUrl: window.location.href,
            },
          });

          if (res?.data?.url) {
            window.open(res.data.url, "_self");
          }
        },
        revenueCatRestorePurchase: async () => {
          if (!useAuth.isAuthenticated()) return;

          const restorePurchaseResponse = await Purchases.restorePurchases();
          if (
            !restorePurchaseResponse?.customerInfo?.activeSubscriptions ||
            restorePurchaseResponse?.customerInfo?.activeSubscriptions
              ?.length === 0
          ) {
            toast.error(i18n.t("useUserSlice.noSubscriptionText"));
            return;
          }

          const isSubscribed =
            restorePurchaseResponse.customerInfo.activeSubscriptions.length > 0;
          set({ isSubscribed });

          toast.success(i18n.t("useUserSlice.restorePurchaseSuccess"));
        },
      }),

      reset: () => {
        set({
          userToken: undefined,
          isSubscribed: false,
          userData: undefined,
          chats: [],
          chatsNextCursor: undefined,
        });
      },
    }),
    {
      name: `deepseek-${Capacitor.getPlatform()}-user-storage`,
      storage: createJSONStorage(() => storage),
      onRehydrateStorage: () => (state) => {
        state && state.setHasHydrated(true);
      },
    }
  )
);
